/**
 * useScanListener — OS-INV-12 / MADE-290 / MADE-292
 *
 * Document-level keyboard listener that detects scanner-wedge input.
 * Scanners type characters faster than INTER_CHAR_MS; humans do not.
 *
 * MADE-292: Accepts an optional `onCountSession` callback. When a scanned
 * value matches the count-session URL pattern (/os/count/:sessionId), that
 * callback fires instead of `onScan`.
 */

import { useCallback, useEffect, useRef } from 'react';

const INTER_CHAR_MS = 50; // scanners type faster than humans

/** Pattern that identifies a count-session QR: /os/count/<sessionId> */
// F1 fix: require exactly 32 hex chars (crypto.randomBytes(16).toString('hex')).
// The previous open-ended match fired on the first character of the session ID,
// causing the listener to call onCountSession('/os/count/6') on a wedge scan.
const COUNT_SESSION_RE = /\/os\/count\/([0-9a-f]{32})(?:[^0-9a-f]|$)/i;

/**
 * @param onScan         - Called with the raw buffer value for non-count scans.
 * @param onCountSession - Called with the sessionId when a count-session URL is detected.
 */
export function useScanListener(
  onScan: (value: string) => void,
  onCountSession?: (sessionId: string) => void,
) {
  const buffer = useRef('');
  const lastKeyTime = useRef(0);

  const handler = useCallback(
    (e: KeyboardEvent) => {
      // Don't intercept when user is typing in an input/textarea
      const tag = (document.activeElement?.tagName ?? '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;

      const now = Date.now();

      if (e.key === 'Enter') {
        if (buffer.current.length > 2) {
          const value = buffer.current;
          const countMatch = value.match(COUNT_SESSION_RE);
          if (countMatch && onCountSession) {
            onCountSession(countMatch[1].toLowerCase()); // Bug 1: session IDs are hex; scanner toUpperCase corrupts them
          } else {
            onScan(value);
          }
        }
        buffer.current = '';
        return;
      }

      // Reset buffer if gap is too large (human typing speed)
      if (now - lastKeyTime.current > INTER_CHAR_MS && buffer.current.length > 0) {
        buffer.current = '';
      }

      if (e.key.length === 1) buffer.current += e.key;
      lastKeyTime.current = now;
    },
    [onScan, onCountSession],
  );

  useEffect(() => {
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [handler]);
}
